'use client'

import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import {
  Children,
  type CSSProperties,
  type JSX,
  type ReactNode,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { Swiper as SwiperInstance } from 'swiper'
import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { SwiperProps } from 'swiper/react'

export type CarouselNavigationRenderProps = {
  isBeginning: boolean
  isEnd: boolean
  slideNext: () => void
  slidePrev: () => void
}

type NavigationRenderer = (props: CarouselNavigationRenderProps) => ReactNode

export type CarouselCommonProps = {
  allowTouchMove?: boolean
  breakpoints?: SwiperProps['breakpoints']
  className?: string
  onReachEnd?: () => void
  renderNavigation?: NavigationRenderer
  slideClassName?: string
  slideStyle?: CSSProperties
  slidesPerView?: SwiperProps['slidesPerView']
  spaceBetween?: number
  sx?: SxProps<Theme>
  watchOverflow?: boolean
}

export type CarouselAdapterProps = CarouselCommonProps & {
  slides: ReactNode[]
}

export type CarouselAdapter = (props: CarouselAdapterProps) => JSX.Element

export type CarouselProps = CarouselCommonProps & {
  adapter?: CarouselAdapter
  children: ReactNode | ReactNode[]
}

const defaultNavState = { isBeginning: true, isEnd: false }

export const SwiperCarouselAdapter: CarouselAdapter = ({
  allowTouchMove = true,
  breakpoints,
  className,
  onReachEnd,
  renderNavigation,
  slideClassName,
  slideStyle,
  slides,
  slidesPerView = 'auto',
  spaceBetween,
  sx,
  watchOverflow = true,
}) => {
  const [swiper, setSwiper] = useState<SwiperInstance | null>(null)
  const [navState, setNavState] = useState(defaultNavState)

  const syncNavState = useCallback((instance: SwiperInstance) => {
    setNavState((prev) =>
      prev.isBeginning === instance.isBeginning && prev.isEnd === instance.isEnd
        ? prev
        : { isBeginning: instance.isBeginning, isEnd: instance.isEnd },
    )
  }, [])

  const slidesKey = useMemo(
    () =>
      slides.map((slide) => (isValidElement(slide) ? String(slide.key) : String(slide))).join('|'),
    [slides],
  )

  useEffect(() => {
    if (!swiper) return
    swiper.update()
    syncNavState(swiper)
  }, [slidesKey, swiper, syncNavState])

  const navigation = useMemo(() => {
    if (!renderNavigation || !swiper) return null
    const slidePrev = () => swiper.slidePrev()
    const slideNext = () => swiper.slideNext()
    return renderNavigation({
      isBeginning: navState.isBeginning,
      isEnd: navState.isEnd,
      slideNext,
      slidePrev,
    })
  }, [navState.isBeginning, navState.isEnd, renderNavigation, swiper])

  return (
    <Box
      className={className}
      position="relative"
      sx={{
        width: '100%',
        overflow: 'hidden',
        ...sx,
      }}>
      <Swiper
        allowTouchMove={allowTouchMove}
        breakpoints={breakpoints}
        onReachEnd={() => onReachEnd?.()}
        onSlideChange={syncNavState}
        onResize={syncNavState}
        onUpdate={syncNavState}
        onSwiper={(instance) => {
          setSwiper(instance)
          syncNavState(instance)
        }}
        slidesPerView={slidesPerView}
        spaceBetween={spaceBetween}
        watchOverflow={watchOverflow}>
        {slides.map((slide) => (
          <SwiperSlide
            className={slideClassName}
            key={isValidElement(slide) ? String(slide.key) : String(slide)}
            style={{
              width: slidesPerView === 'auto' ? 'auto' : undefined,
              ...slideStyle,
            }}>
            {slide}
          </SwiperSlide>
        ))}
      </Swiper>
      {navigation}
    </Box>
  )
}

export const Carousel = ({
  adapter: Adapter = SwiperCarouselAdapter,
  allowTouchMove,
  breakpoints,
  children,
  className,
  onReachEnd,
  renderNavigation,
  slideClassName,
  slideStyle,
  slidesPerView,
  spaceBetween,
  sx,
  watchOverflow,
}: CarouselProps) => {
  const slides = useMemo(() => Children.toArray(children), [children])

  if (!slides.length) return null

  return (
    <Adapter
      allowTouchMove={allowTouchMove}
      breakpoints={breakpoints}
      className={className}
      onReachEnd={onReachEnd}
      renderNavigation={renderNavigation}
      slideClassName={slideClassName}
      slideStyle={slideStyle}
      slides={slides}
      slidesPerView={slidesPerView}
      spaceBetween={spaceBetween}
      sx={sx}
      watchOverflow={watchOverflow}
    />
  )
}

export default Carousel
